/**
 * Drawer Builder — shared constants (single source of truth).
 *
 * Loaded as a classic deferred script BEFORE the other builder scripts so
 * both the ES module (drawer-builder.js) and the jQuery IIFEs
 * (drawer-builder-legacy*.js) can read it as `window.MD_CONST`.
 *
 * Consumers MUST keep a literal fallback at the call site
 * (e.g. `(window.MD_CONST && window.MD_CONST.GRID_UNIT_MM) || 29.5`) so a
 * load-order miss can never change placement math.
 */
(function () {
  'use strict';

  window.MD_CONST = window.MD_CONST || {
    // Physical size of one grid cell, in millimetres. Drawer dimensions (mm)
    // are divided by this to derive block counts, and block counts multiplied
    // by it to render size badges.
    GRID_UNIT_MM: 29.5,

    // True-to-Size shares the internal geometry planner's hard fulfilment
    // budget: at most 48 cells / 1,416 mm on either axis and 30 total 7 x 7
    // baseplate panels. Storefront eligibility must never exceed these values.
    TTS_MAX_DIMENSION_MM: 1416,
    TTS_PANEL_MAX_CELLS: 7,
    TTS_MAX_PANELS: 30,

    // Module height is fixed across the catalogue (used in size badges).
    MODULE_HEIGHT_MM: 45,

    // Mobile/bottom-sheet layout breakpoint (max-width, px). The builder uses the
    // canvas-first bottom-sheet products panel at or below this width; the desktop
    // side-panel layout takes over at DESKTOP_BREAKPOINT (one px above). Kept
    // ADJACENT (999/1000) so there is no unhandled "tablet gap" — tablets render
    // the bottom-sheet like large phones. The CSS `@media (max-width: 999px)` /
    // `(min-width: 1000px)` builder rules must match these values.
    MOBILE_BREAKPOINT: 999,

    // Desktop layout breakpoint (min-width, px). Exactly one px above
    // MOBILE_BREAKPOINT so the two layouts meet with no gap.
    DESKTOP_BREAKPOINT: 1000,

    // Products-panel expand/collapse animation duration (ms). Matches the
    // CSS transition (~300ms) with a small buffer; mirrored in
    // drawer-builder-legacy.js (ANIM_MS).
    ANIM_MS: 320,

    // Collapsed products-sheet peek bounds (mobile). The peek is DYNAMIC: it
    // fills the vertical space the drawer canvas doesn't use, so a small drawer
    // flexes the products up to fill the white space and a tall drawer shrinks
    // them to a strip. Bounded by [MIN, MAX] where MIN keeps the grabber +
    // colour row visible and MAX (a fraction of the viewport) stops the products
    // from overpowering the canvas. The peek setter (updateMobileSheetPeek)
    // reads the ceiling via window.mdPeekMax(); the canvas fit-to-height reserve
    // uses MIN so the canvas is maximised and the peek fills whatever's left.
    SHEET_PEEK_MIN_PX: 94,
    SHEET_PEEK_MAX_VH_RATIO: 0.45,
  };

  /**
   * Lightweight haptic feedback (the web echo of Forage's
   * UIImpactFeedbackGenerator). `navigator.vibrate` is supported on
   * Chrome/Android only — iOS Safari ignores it, so the visual press-scale
   * carries the feedback there. Always guarded; never throws. `pattern` is a
   * ms duration or a vibration pattern array.
   */
  window.mdHaptic = window.mdHaptic || function (pattern) {
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(pattern == null ? 10 : pattern);
      }
    } catch (e) {
      /* no-op — unsupported / blocked */
    }
  };

  /**
   * Leftover gap (mm) on one axis after the drawer is rounded DOWN to whole
   * grid blocks: `mm - floor(mm / GRID_UNIT_MM) * GRID_UNIT_MM` (range 0 ..
   * ~29.4). A fitted build uses this remainder to derive its versioned
   * edge-module and baseplate-panel adjustment while retaining the designed
   * wall clearance. Keeps the literal 29.5 fallback at the call site so a
   * load-order miss can't change the math.
   */
  window.mdLeftoverMm = window.mdLeftoverMm || function (mm) {
    var u = (window.MD_CONST && window.MD_CONST.GRID_UNIT_MM) || 29.5;
    var v = parseFloat(mm) || 0;
    return v - Math.floor(v / u) * u;        // 0 .. ~29.4
  };

  /**
   * Shared storefront eligibility guard for the fitted fulfilment contract.
   * The returned reason is stable so onboarding can explain why the fitted
   * choice is unavailable while the persistent builder control simply hides.
   */
  window.mdTtsEligibility = window.mdTtsEligibility || function (lengthMM, widthMM, gapThresholdMM) {
    var C = window.MD_CONST || {};
    var unit = C.GRID_UNIT_MM || 29.5;
    var maxDimension = C.TTS_MAX_DIMENSION_MM || 1416;
    var panelCells = C.TTS_PANEL_MAX_CELLS || 7;
    var maxPanels = C.TTS_MAX_PANELS || 30;
    var threshold = Number(gapThresholdMM);
    if (!isFinite(threshold)) threshold = 2;
    var length = Number(lengthMM);
    var width = Number(widthMM);

    if (!isFinite(length) || !isFinite(width) || length < unit || width < unit) {
      return { eligible: false, reason: 'minimum_grid', panelCount: 0 };
    }
    if (length > maxDimension || width > maxDimension) {
      return { eligible: false, reason: 'max_dimension', panelCount: 0 };
    }

    var rows = Math.floor(length / unit);
    var cols = Math.floor(width / unit);
    var panelCount = Math.ceil(rows / panelCells) * Math.ceil(cols / panelCells);
    if (panelCount > maxPanels) {
      return { eligible: false, reason: 'max_panels', panelCount: panelCount };
    }

    var gapLength = length - rows * unit;
    var gapWidth = width - cols * unit;
    if (Math.max(gapLength, gapWidth) <= threshold) {
      return { eligible: false, reason: 'gap_threshold', panelCount: panelCount };
    }
    return { eligible: true, reason: null, panelCount: panelCount };
  };

  /**
   * Ceiling (px) for the collapsed products-sheet peek, given a viewport
   * height: round(vpH * SHEET_PEEK_MAX_VH_RATIO), floored at MIN. The peek
   * itself is dynamic (fills the space the canvas leaves) — this only bounds it
   * so the products can't overpower the canvas. Derived from the viewport ONLY
   * (never the live, gesture-mutated --db-sheet-peek-h). Literal fallbacks per
   * the contract above.
   */
  window.mdPeekMax = window.mdPeekMax || function (viewportH) {
    var C = window.MD_CONST || {};
    var ratio = C.SHEET_PEEK_MAX_VH_RATIO || 0.45;
    var min = C.SHEET_PEEK_MIN_PX || 94;
    var vh = (typeof viewportH === 'number' && isFinite(viewportH)) ? viewportH : 0;
    if (vh <= 0) return min;
    return Math.max(min, Math.round(vh * ratio));
  };
})();
